// Клиент для `/api/v1/tasks` и `/api/v1/queue`.
//
// Org_id backend читает из JWT-токена (`Principal.org_id`), header
// `X-Org-Id` не передаётся. Идентификация запросов — через `authedFetch`,
// который шлёт HttpOnly session-cookie (`credentials: "include"`).

import { apiErrorFromResponse } from "../../api/apiError";
import { authedFetch } from "../../auth/authedFetch";

export type TaskStatus =
  | "queued"
  | "draft"
  | "awaiting_plan_approval"
  | "planned"
  | "in_progress"
  | "ci_running"
  | "review"
  | "awaiting_approval"
  | "done"
  | "cancelled"
  | "escalated";

export type TaskKind = "epic" | "atomic";

// Поле сортировки списка задач (зеркало backend `TaskSortField`):
// created_at — по времени (FIFO), status — по стадии FSM.
export type TaskSortField = "created_at" | "status";

// Направление сортировки (зеркало backend `SortOrder`).
export type SortOrder = "asc" | "desc";

// Опции выборки списка задач для `GET /api/v1/tasks`. Все поля
// опциональны: пустой объект = дефолтный список (created_at asc, без
// фильтра по статусу).
export interface TaskListOptions {
  // Только активные задачи (нетерминальный статус) — тумблер очереди.
  readonly activeOnly?: boolean;
  // Фильтр по статусу: один или несколько статусов FSM (повторяемый
  // query-параметр `status` → IN-clause на backend). Пусто/undefined —
  // без фильтра.
  readonly statuses?: readonly TaskStatus[];
  // Поле сортировки. undefined — backend-дефолт created_at.
  readonly sort?: TaskSortField;
  // Направление сортировки. undefined — backend-дефолт asc.
  readonly order?: SortOrder;
}

export interface TaskResponse {
  readonly id: string;
  readonly org_id: string;
  readonly queue_id: string;
  readonly runner_id: string | null;
  readonly kind: TaskKind;
  readonly title: string;
  // PR/repo поля — Optional: NULL для ручных задач или до активации coder'ом.
  readonly body: string | null;
  readonly repository_full_name: string | null;
  readonly issue_number: number | null;
  readonly head_branch: string | null;
  readonly pr_url: string | null;
  readonly pr_number: number | null;
  // Момент, когда фаза 1 создания PR закоммитила отметку «создание
  // начато» — до обращения к хостингу (ADR-0058). null — создание PR
  // ещё не пыталось начаться. Сама по себе пара «отметка есть, номера
  // нет» НЕ признак осиротевшего PR (она истинна и во время
  // совершенно нормального создания) — предикат-дискриминатор в
  // `features/queue/unrecordedPrAttempt.ts`.
  readonly pr_creation_started_at: string | null;
  readonly status: TaskStatus;
  readonly is_paused: boolean;
  readonly is_cancelled: boolean;
  readonly is_failed: boolean;
  readonly is_escalated: boolean;
  // Зеркало `task.escalation_reason`; осмысленно при `status === "escalated"`:
  // после разбора эскалации поле хранит обоснование решения.
  readonly escalation_reason: string | null;
  // Будет ли задача взята в работу: для QUEUED — нет открытого прямого
  // блокера и не external; для прочих статусов — всегда false
  // (actionable релевантен только для очереди). Зеркало
  // `TaskResponse.is_actionable` backend (ADR-0006, спека
  // `04-integrations/label-semantics.md` § «UI-фильтр»).
  readonly is_actionable: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

// Лог переходов task.status из task_status_event.
export type ActorKind = "user" | "agent" | "system";

export interface TaskStatusEventResponse {
  readonly id: string;
  readonly task_id: string;
  readonly from_status: TaskStatus | null;
  readonly to_status: TaskStatus;
  readonly reason: string | null;
  readonly actor_kind: ActorKind;
  readonly actor_id: string | null;
  readonly at: string;
}

export interface TaskQueueResponse {
  readonly id: string;
  readonly org_id: string;
  readonly runner_id: string;
  readonly active_task_id: string | null;
  readonly ordering_policy: "fifo";
  // Состояние пауз очереди (operator drain / quota soft pause). Оба
  // независимы; активация требует, чтобы оба гейта были открыты.
  readonly paused_by_operator: boolean;
  readonly paused_by_quota: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

// Диспозиция прерывания активной задачи (см. backend
// `InterruptDisposition`): paused — неразрушающий дефолт, cancelled —
// разрушающее.
export type InterruptDisposition = "paused" | "cancelled";

export interface QueueControlResult {
  readonly paused_by_operator: boolean;
  readonly interrupted_active: boolean;
  readonly active_disposition: InterruptDisposition | null;
}

export interface OrgPauseResult {
  readonly paused_queue_count: number;
}

export interface TaskCreateRequest {
  readonly title: string;
  readonly kind: TaskKind;
}

// Зеркало `backend/modules/task/schemas/plan.py` поле в поле:
// `PlanSubtaskView`, `PendingPlanResponse` — тело `GET /tasks/{id}/plan`.
// `depends_on` бэкенд не отдаёт.
export interface PlanSubtaskView {
  readonly title: string;
  readonly description: string;
  readonly acceptance: readonly string[];
  readonly plan_key: string;
}

export interface PendingPlanResponse {
  readonly task_id: string;
  readonly subtasks: readonly PlanSubtaskView[];
  readonly checklist: readonly string[];
  readonly approval_text: string;
}

// Зеркало backend-схем `QuotaWindowResponse` / `QuotasResponse`
// (`GET /api/v1/quotas`). `percent` — 0..100 относительно лимита окна;
// `threshold_percent` — порог soft pause (UI красит бар в alert-цвет
// при percent >= порога); `hours_to_pause` — прогноз часов до паузы
// при текущей скорости (null — нет потребления / уже за порогом /
// нет данных, UI рисует «—»).
export interface QuotaWindow {
  readonly accumulated_tokens: number;
  readonly percent: number;
  readonly threshold_percent: number;
  readonly hours_to_pause: number | null;
}

export interface QuotasResponse {
  readonly session: QuotaWindow;
  readonly weekly: QuotaWindow;
  // Пер-модельные под-окна (ключ — id модели, например "opus"), живущие
  // параллельно аккаунтным session/weekly. Пусто, если пер-модельных
  // сигналов ещё не было.
  readonly per_model: Record<string, QuotaWindow>;
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// Дефолты сортировки совпадают с backend-дефолтами
// (`GET /api/v1/tasks`) — при них параметры опускаем, чтобы URL для
// обычного просмотра очереди оставался чистым (`/api/v1/tasks`).
const DEFAULT_SORT: TaskSortField = "created_at";
const DEFAULT_ORDER: SortOrder = "asc";

// Собирает query-string для `GET /api/v1/tasks` из опций выборки.
// `status` — повторяемый параметр (по одному на статус → IN-clause).
// Дефолтные значения (activeOnly=false, сортировка created_at/asc)
// опускаются.
function buildTaskListQuery(options: TaskListOptions): string {
  const params = new URLSearchParams();
  if (options.activeOnly === true) {
    params.append("active_only", "true");
  }
  for (const status of options.statuses ?? []) {
    params.append("status", status);
  }
  if (options.sort !== undefined && options.sort !== DEFAULT_SORT) {
    params.append("sort", options.sort);
  }
  if (options.order !== undefined && options.order !== DEFAULT_ORDER) {
    params.append("order", options.order);
  }
  const query = params.toString();
  return query.length > 0 ? `?${query}` : "";
}

// Список задач org'а. `options` управляет фильтром по статусу,
// сортировкой (поле + направление) и тумблером active_only; пустой
// объект (дефолт) — весь список в backend-дефолтном порядке
// (created_at asc, FIFO-очередь).
export async function fetchTasks(
  options: TaskListOptions = {},
): Promise<TaskResponse[]> {
  const response = await authedFetch(
    `/api/v1/tasks${buildTaskListQuery(options)}`,
    {
      headers: buildHeaders(),
    },
  );
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "tasks");
  }
  return (await response.json()) as TaskResponse[];
}

// Ответ `GET /api/v1/queue`. `queue === null` — у организации нет
// раннера (штатное состояние чистой установки, не ошибка): раннер и
// его очередь заводятся регистрацией в «Настройки → Раннеры», до
// неё ручка честно отвечает `200` с пустым значением.
export interface QueueStateResponse {
  readonly queue: TaskQueueResponse | null;
}

export async function fetchQueue(): Promise<QueueStateResponse> {
  const response = await authedFetch("/api/v1/queue", {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "queue");
  }
  return (await response.json()) as QueueStateResponse;
}

// Сводка квот org'а для UI-индикатора.
export async function fetchQuotas(): Promise<QuotasResponse> {
  const response = await authedFetch("/api/v1/quotas", {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "quotas");
  }
  return (await response.json()) as QuotasResponse;
}

// 409 `no_runner` — у организации нет неснятой очереди (раннера).
export async function createTask(
  body: TaskCreateRequest,
): Promise<TaskResponse> {
  const response = await authedFetch("/api/v1/tasks", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "createTask");
  }
  return (await response.json()) as TaskResponse;
}

// 404 `task_not_found` — задачи нет или она чужой организации.
export async function fetchTask(taskId: string): Promise<TaskResponse> {
  const response = await authedFetch(`/api/v1/tasks/${taskId}`, {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "task");
  }
  return (await response.json()) as TaskResponse;
}

const NOT_FOUND_STATUS = 404;
const PLAN_NOT_FOUND = "plan_not_found";

// Pending-план эпика. Lazy — грузится только на детальной странице.
// 404 `plan_not_found` — штатное «pending-плана нет» (эпик в draft, после
// одобрения/отклонения и т.п.): `null`. Прочие отказы, в т.ч. 404 без
// кода, — `ApiError`.
export async function fetchTaskPlan(
  taskId: string,
): Promise<PendingPlanResponse | null> {
  const response = await authedFetch(`/api/v1/tasks/${taskId}/plan`, {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    const error = await apiErrorFromResponse(response, "task plan");
    if (error.status === NOT_FOUND_STATUS && error.code === PLAN_NOT_FOUND) {
      return null;
    }
    throw error;
  }
  return (await response.json()) as PendingPlanResponse;
}

export async function fetchTaskEvents(
  taskId: string,
): Promise<TaskStatusEventResponse[]> {
  const response = await authedFetch(`/api/v1/tasks/${taskId}/events`, {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "task events");
  }
  return (await response.json()) as TaskStatusEventResponse[];
}

// POST-действие над задачей: 204 — успех, иначе `ApiError` (статус + `code`
// отказа; 409 — действие недопустимо, причина — в `code`).
async function postTaskAction(
  taskId: string,
  action: string,
  label: string,
): Promise<void> {
  const response = await authedFetch(`/api/v1/tasks/${taskId}/${action}`, {
    method: "POST",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, label);
  }
}

// Отмена задачи: idempotent no-op для уже-CANCELLED, 409 `task_done` — задача
// в DONE.
export async function cancelTask(taskId: string): Promise<void> {
  await postTaskAction(taskId, "cancel", "cancelTask");
}

// Ручной перезапуск: no-op для уже-QUEUED. 409 `task_not_rerunnable` — задача
// не в escalated/cancelled (перечитать); 409 `queue_retired` — очередь снята,
// повтор не поможет.
export async function rerunTask(taskId: string): Promise<void> {
  await postTaskAction(taskId, "rerun", "rerunTask");
}

// Снятие паузы задачи: 409 `task_not_paused` — не на паузе; статус и очередь
// не меняются.
export async function resumeTask(taskId: string): Promise<void> {
  await postTaskAction(taskId, "resume", "resumeTask");
}

// Операторские контролы очереди (см. backend
// `modules/task/http/queue_controls.py`, ADR-0018). Все возвращают 200 с
// машиночитаемым итогом действия; pause/resume/interrupt/stop — 409
// `no_runner`, если у организации нет раннера.

// Ручная пауза очереди (drain): новые queued не берутся, активная
// доигрывает.
export async function pauseQueue(): Promise<QueueControlResult> {
  const response = await authedFetch("/api/v1/queue/pause", {
    method: "POST",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "pauseQueue");
  }
  return (await response.json()) as QueueControlResult;
}

// Снять ручную паузу очереди + штатная попытка активации следующей.
export async function resumeQueue(): Promise<QueueControlResult> {
  const response = await authedFetch("/api/v1/queue/resume", {
    method: "POST",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "resumeQueue");
  }
  return (await response.json()) as QueueControlResult;
}

// Прервать активную задачу очереди с диспозицией (дефолт paused).
export async function interruptQueue(
  disposition: InterruptDisposition = "paused",
): Promise<QueueControlResult> {
  const response = await authedFetch("/api/v1/queue/interrupt", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ disposition }),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "interruptQueue");
  }
  return (await response.json()) as QueueControlResult;
}

// Стоп очереди = пауза (drain) + прерывание активной задачи.
export async function stopQueue(
  disposition: InterruptDisposition = "paused",
): Promise<QueueControlResult> {
  const response = await authedFetch("/api/v1/queue/stop", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ disposition }),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "stopQueue");
  }
  return (await response.json()) as QueueControlResult;
}

// Org-wide пауза — веер ручной паузы по всем очередям организации.
export async function pauseAllQueues(): Promise<OrgPauseResult> {
  const response = await authedFetch("/api/v1/queue/pause_all", {
    method: "POST",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "pauseAllQueues");
  }
  return (await response.json()) as OrgPauseResult;
}

// Одобрение плана декомпозиции. 204 No Content на успех.
// Отказ — `ApiError`: 404 `task_not_found`; 409 `not_awaiting_plan_approval`
// (task не ждёт одобрения) или `no_pending_plan`; 422 `no_repository`,
// `no_tracker_connection`, `dependency_cycle`.
export async function approvePlan(taskId: string): Promise<void> {
  const response = await authedFetch(`/api/v1/tasks/${taskId}/approve_plan`, {
    method: "POST",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "approvePlan");
  }
}

// Отклонение плана декомпозиции. reason — опциональная строка.
// 204 No Content на успех. Отказ — `ApiError`: 404 `task_not_found`;
// 409 `not_awaiting_plan_approval`.
export async function rejectPlan(
  taskId: string,
  reason?: string,
): Promise<void> {
  const body =
    reason !== undefined && reason.length > 0
      ? JSON.stringify({ reason })
      : "{}";
  const response = await authedFetch(`/api/v1/tasks/${taskId}/reject_plan`, {
    method: "POST",
    headers: buildHeaders(),
    body,
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "rejectPlan");
  }
}
